import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  StickyNote, MessageSquare, X, Plus, Pin, PinOff, Trash2, 
  Send, Clock, Minimize2, Maximize2, CheckCircle, Circle, 
  Edit3, Save, Users, RefreshCw
} from 'lucide-react';
import { format, isToday, isYesterday, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

// Note Component
const NoteItem = ({ note, onTogglePin, onDelete, onToggleComplete, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.content);

  const handleSave = () => {
    if (editContent.trim() && editContent !== note.content) {
      onUpdate(note.id, editContent);
    }
    setIsEditing(false);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -10 }}
      className={`group relative p-3 rounded-xl border transition-all ${
        note.pinned 
          ? 'bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/30' 
          : 'bg-zinc-800/50 border-zinc-700/50 hover:border-zinc-600'
      } ${note.completed ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start gap-2">
        <button 
          onClick={() => onToggleComplete(note.id)}
          className="mt-0.5 shrink-0"
        >
          {note.completed ? (
            <CheckCircle size={16} className="text-green-500" />
          ) : (
            <Circle size={16} className="text-zinc-500 hover:text-zinc-300" />
          )}
        </button>
        
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full bg-transparent text-sm text-zinc-300 resize-none focus:outline-none"
              rows={2}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSave();
                }
              }}
            />
          ) : (
            <p className={`text-sm text-zinc-300 break-words ${note.completed ? 'line-through' : ''}`}>
              {note.content}
            </p>
          )}
          <p className="text-[10px] text-zinc-600 mt-1.5">
            {format(new Date(note.created_at), 'd MMM, HH:mm', { locale: es })}
          </p>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          {isEditing ? (
            <button 
              onClick={handleSave}
              className="w-6 h-6 rounded-lg hover:bg-green-500/20 flex items-center justify-center"
            >
              <Save size={12} className="text-green-400" />
            </button>
          ) : (
            <button 
              onClick={() => setIsEditing(true)}
              className="w-6 h-6 rounded-lg hover:bg-zinc-700 flex items-center justify-center"
            >
              <Edit3 size={12} className="text-zinc-400" />
            </button>
          )}
          <button 
            onClick={() => onTogglePin(note.id)}
            className="w-6 h-6 rounded-lg hover:bg-zinc-700 flex items-center justify-center"
          >
            {note.pinned ? (
              <PinOff size={12} className="text-amber-400" />
            ) : (
              <Pin size={12} className="text-zinc-400" />
            )}
          </button>
          <button 
            onClick={() => onDelete(note.id)}
            className="w-6 h-6 rounded-lg hover:bg-red-500/20 flex items-center justify-center"
          >
            <Trash2 size={12} className="text-red-400" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Format message date
const formatMessageDate = (dateStr) => {
  const date = new Date(dateStr);
  if (isToday(date)) {
    return format(date, 'HH:mm');
  } else if (isYesterday(date)) {
    return `Ayer ${format(date, 'HH:mm')}`;
  }
  return format(date, 'd MMM HH:mm', { locale: es });
};

// Chat Message Component
const ChatMessage = ({ message, isOwn, showAvatar = true }) => {
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      'bg-purple-500', 'bg-blue-500', 'bg-green-500', 
      'bg-amber-500', 'bg-rose-500', 'bg-cyan-500'
    ];
    const index = name ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex gap-2 mb-3 ${isOwn ? 'flex-row-reverse' : ''}`}
    >
      {/* Avatar */}
      {showAvatar && !isOwn && (
        <div className={`w-8 h-8 rounded-full ${getAvatarColor(message.sender_name)} flex items-center justify-center shrink-0`}>
          <span className="text-[10px] font-bold text-white">
            {getInitials(message.sender_name)}
          </span>
        </div>
      )}
      {!showAvatar && !isOwn && <div className="w-8 shrink-0" />}

      {/* Message bubble */}
      <div className={`max-w-[75%] ${isOwn ? 'items-end' : 'items-start'}`}>
        {/* Sender name (only for others) */}
        {showAvatar && !isOwn && (
          <p className="text-[10px] text-zinc-500 mb-1 ml-1 flex items-center gap-1">
            <span className="font-medium text-zinc-400">{message.sender_name}</span>
            {message.sender_role && (
              <span className="text-zinc-600">• {message.sender_role}</span>
            )}
          </p>
        )}
        
        <div className={`px-3 py-2 rounded-2xl ${
          isOwn 
            ? 'bg-gradient-to-r from-[#D4AF37] to-[#B8962D] text-black rounded-br-sm' 
            : 'bg-zinc-800 text-zinc-200 rounded-bl-sm'
        }`}>
          <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
        </div>
        
        {/* Timestamp */}
        <p className={`text-[10px] text-zinc-600 mt-0.5 ${isOwn ? 'text-right mr-1' : 'ml-1'}`}>
          {formatMessageDate(message.created_at)}
        </p>
      </div>
    </motion.div>
  );
};

// Date Separator
const DateSeparator = ({ date }) => (
  <div className="flex items-center gap-3 my-4">
    <div className="flex-1 h-px bg-zinc-800" />
    <span className="text-[10px] text-zinc-600 uppercase tracking-wider">
      {isToday(new Date(date)) 
        ? 'Hoy' 
        : isYesterday(new Date(date)) 
          ? 'Ayer' 
          : format(new Date(date), 'd MMMM yyyy', { locale: es })}
    </span>
    <div className="flex-1 h-px bg-zinc-800" />
  </div>
);

// Main Widget Component
const FloatingWorkspaceWidget = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('notes');
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  
  const chatEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Get current user ID
  const currentUserId = user?.id || user?.user_id;

  // Load notes from localStorage
  useEffect(() => {
    const savedNotes = localStorage.getItem('admin_notes');
    if (savedNotes) {
      try {
        setNotes(JSON.parse(savedNotes));
      } catch (e) {
        console.error('Error loading notes:', e);
      }
    }
  }, []);

  // Save notes to localStorage
  useEffect(() => {
    localStorage.setItem('admin_notes', JSON.stringify(notes));
  }, [notes]);

  // Fetch chat messages
  const fetchMessages = useCallback(async () => {
    try {
      const response = await api.get('/internal-chat/messages?limit=100');
      setMessages(response.data.messages || []);
      
      // Mark messages as read
      const unreadMessages = (response.data.messages || []).filter(
        m => !m.read_by?.includes(currentUserId) && m.sender_id !== currentUserId
      );
      
      // Mark each unread message as read
      for (const msg of unreadMessages) {
        try {
          await api.post(`/internal-chat/messages/${msg.id}/read`);
        } catch (e) {
          // Ignore read marking errors
        }
      }
      
      if (activeTab === 'chat') {
        setUnreadCount(0);
      }
    } catch (error) {
      console.log('Could not fetch chat messages');
    }
  }, [currentUserId, activeTab]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await api.get('/internal-chat/unread-count');
      if (activeTab !== 'chat' || !isOpen) {
        setUnreadCount(response.data.unread_count || 0);
      }
    } catch (error) {
      // Silently fail
    }
  }, [activeTab, isOpen]);

  // Fetch online users
  const fetchOnlineUsers = useCallback(async () => {
    try {
      const response = await api.get('/internal-chat/online-users');
      setOnlineUsers(response.data.users || []);
    } catch (error) {
      // Silently fail
    }
  }, []);

  // Initial load and polling
  useEffect(() => {
    if (isOpen && activeTab === 'chat') {
      setLoadingMessages(true);
      fetchMessages().finally(() => setLoadingMessages(false));
      fetchOnlineUsers();
      
      // Poll for new messages every 5 seconds
      pollIntervalRef.current = setInterval(() => {
        fetchMessages();
      }, 5000);
    } else {
      // Just fetch unread count when not in chat
      fetchUnreadCount();
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, activeTab, fetchMessages, fetchUnreadCount, fetchOnlineUsers]);

  // Scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current && activeTab === 'chat') {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeTab]);

  // Note handlers
  const handleAddNote = () => {
    if (!newNote.trim()) return;
    
    const note = {
      id: Date.now().toString(),
      content: newNote.trim(),
      pinned: false,
      completed: false,
      created_at: new Date().toISOString()
    };
    
    setNotes(prev => [note, ...prev]);
    setNewNote('');
    toast.success('Nota agregada');
  };

  const handleTogglePin = (noteId) => {
    setNotes(prev => prev.map(note => 
      note.id === noteId ? { ...note, pinned: !note.pinned } : note
    ).sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)));
  };

  const handleDeleteNote = (noteId) => {
    setNotes(prev => prev.filter(note => note.id !== noteId));
    toast.success('Nota eliminada');
  };

  const handleToggleComplete = (noteId) => {
    setNotes(prev => prev.map(note => 
      note.id === noteId ? { ...note, completed: !note.completed } : note
    ));
  };

  const handleUpdateNote = (noteId, newContent) => {
    setNotes(prev => prev.map(note => 
      note.id === noteId ? { ...note, content: newContent } : note
    ));
  };

  // Chat handlers
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    const content = newMessage.trim();
    setNewMessage('');
    
    // Optimistic update
    const tempMessage = {
      id: `temp-${Date.now()}`,
      content,
      sender_id: currentUserId,
      sender_name: user?.full_name || user?.username || 'Tú',
      sender_role: user?.role_name || 'Admin',
      created_at: new Date().toISOString(),
      read_by: [currentUserId]
    };
    
    setMessages(prev => [...prev, tempMessage]);
    
    try {
      const response = await api.post('/internal-chat/messages', { content });
      // Replace temp message with real one
      setMessages(prev => prev.map(m => 
        m.id === tempMessage.id ? response.data.message : m
      ));
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar mensaje');
      // Remove temp message on error
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
    }
  };

  // Sort notes: pinned first, then by date
  const sortedNotes = [...notes].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const pinnedCount = notes.filter(n => n.pinned).length;

  // Group messages by date for separators
  const groupedMessages = messages.reduce((acc, msg, idx) => {
    const msgDate = format(new Date(msg.created_at), 'yyyy-MM-dd');
    const prevMsg = messages[idx - 1];
    const prevDate = prevMsg ? format(new Date(prevMsg.created_at), 'yyyy-MM-dd') : null;
    
    // Add date separator if new day
    if (msgDate !== prevDate) {
      acc.push({ type: 'separator', date: msg.created_at });
    }
    
    // Check if should show avatar (different sender or new day)
    const showAvatar = !prevMsg || prevMsg.sender_id !== msg.sender_id || msgDate !== prevDate;
    
    acc.push({ type: 'message', message: msg, showAvatar });
    return acc;
  }, []);

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8962D] shadow-lg shadow-[#D4AF37]/30 flex items-center justify-center group"
            data-testid="workspace-widget-toggle"
          >
            <div className="relative">
              <MessageSquare size={24} className="text-black" />
              {(notes.length > 0 || unreadCount > 0) && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                  {unreadCount > 0 ? unreadCount : notes.length}
                </span>
              )}
            </div>
            
            {/* Tooltip */}
            <span className="absolute right-full mr-3 px-3 py-1.5 bg-zinc-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Notas y Chat
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Widget Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={`fixed z-50 bg-zinc-900/95 backdrop-blur-xl border border-zinc-800 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden ${
              isExpanded 
                ? 'bottom-4 right-4 left-4 top-20 sm:left-auto sm:w-[500px] sm:top-4' 
                : 'bottom-6 right-6 w-[400px] max-h-[600px]'
            } transition-all duration-300`}
            data-testid="workspace-widget-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-900/80">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30 flex items-center justify-center">
                  {activeTab === 'notes' ? (
                    <StickyNote size={16} className="text-[#D4AF37]" />
                  ) : (
                    <MessageSquare size={16} className="text-[#D4AF37]" />
                  )}
                </div>
                <div>
                  <span className="font-semibold text-white">
                    {activeTab === 'notes' ? 'Mis Notas' : 'Chat del Equipo'}
                  </span>
                  {activeTab === 'chat' && onlineUsers.length > 0 && (
                    <p className="text-[10px] text-zinc-500">
                      {onlineUsers.length} miembros del equipo
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                {activeTab === 'chat' && (
                  <button
                    onClick={() => fetchMessages()}
                    className="w-8 h-8 rounded-lg hover:bg-zinc-800 flex items-center justify-center transition-colors"
                    title="Actualizar"
                  >
                    <RefreshCw size={14} className="text-zinc-400" />
                  </button>
                )}
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="w-8 h-8 rounded-lg hover:bg-zinc-800 flex items-center justify-center transition-colors"
                >
                  {isExpanded ? (
                    <Minimize2 size={16} className="text-zinc-400" />
                  ) : (
                    <Maximize2 size={16} className="text-zinc-400" />
                  )}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-lg hover:bg-zinc-800 flex items-center justify-center transition-colors"
                >
                  <X size={16} className="text-zinc-400" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-zinc-800">
              <button
                onClick={() => setActiveTab('notes')}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors relative ${
                  activeTab === 'notes' ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  <StickyNote size={14} />
                  Notas
                  {notes.length > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-zinc-800 rounded-full">
                      {notes.length}
                    </span>
                  )}
                </span>
                {activeTab === 'notes' && (
                  <motion.div 
                    layoutId="activeWidgetTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" 
                  />
                )}
              </button>
              <button
                onClick={() => { setActiveTab('chat'); setUnreadCount(0); }}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors relative ${
                  activeTab === 'chat' ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  <MessageSquare size={14} />
                  Chat
                  {unreadCount > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-red-500 text-white rounded-full animate-pulse">
                      {unreadCount}
                    </span>
                  )}
                </span>
                {activeTab === 'chat' && (
                  <motion.div 
                    layoutId="activeWidgetTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" 
                  />
                )}
              </button>
            </div>

            {/* Content */}
            <div className={`${isExpanded ? 'h-[calc(100%-140px)]' : 'h-[440px]'} overflow-hidden flex flex-col`}>
              {activeTab === 'notes' ? (
                <>
                  {/* Notes List */}
                  <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    <AnimatePresence mode="popLayout">
                      {sortedNotes.length === 0 ? (
                        <motion.div 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="text-center py-10"
                        >
                          <StickyNote size={32} className="mx-auto text-zinc-700 mb-3" />
                          <p className="text-sm text-zinc-500">Sin notas todavía</p>
                          <p className="text-xs text-zinc-600 mt-1">Escribe tu primera nota abajo</p>
                        </motion.div>
                      ) : (
                        sortedNotes.map((note) => (
                          <NoteItem
                            key={note.id}
                            note={note}
                            onTogglePin={handleTogglePin}
                            onDelete={handleDeleteNote}
                            onToggleComplete={handleToggleComplete}
                            onUpdate={handleUpdateNote}
                          />
                        ))
                      )}
                    </AnimatePresence>
                  </div>

                  {/* New Note Input */}
                  <div className="p-3 border-t border-zinc-800 bg-zinc-900/50">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                        placeholder="Escribe una nota rápida..."
                        className="flex-1 bg-zinc-800/50 border border-zinc-700/50 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#D4AF37]/50"
                      />
                      <button
                        onClick={handleAddNote}
                        disabled={!newNote.trim()}
                        className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#B8962D] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                      >
                        <Plus size={18} className="text-black" />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Chat Messages */}
                  <div className="flex-1 overflow-y-auto p-3">
                    {loadingMessages ? (
                      <div className="flex items-center justify-center h-full">
                        <RefreshCw size={24} className="text-zinc-600 animate-spin" />
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="text-center py-10">
                        <Users size={32} className="mx-auto text-zinc-700 mb-3" />
                        <p className="text-sm text-zinc-500">No hay mensajes aún</p>
                        <p className="text-xs text-zinc-600 mt-1">Sé el primero en escribir</p>
                      </div>
                    ) : (
                      groupedMessages.map((item, idx) => (
                        item.type === 'separator' ? (
                          <DateSeparator key={`sep-${idx}`} date={item.date} />
                        ) : (
                          <ChatMessage 
                            key={item.message.id} 
                            message={item.message}
                            isOwn={item.message.sender_id === currentUserId}
                            showAvatar={item.showAvatar}
                          />
                        )
                      ))
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {/* Chat Input */}
                  <div className="p-3 border-t border-zinc-800 bg-zinc-900/50">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                        placeholder="Escribe un mensaje..."
                        className="flex-1 bg-zinc-800/50 border border-zinc-700/50 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#D4AF37]/50"
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim()}
                        className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#B8962D] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                      >
                        <Send size={16} className="text-black" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pinned Notes Floating (always visible) */}
      <AnimatePresence>
        {!isOpen && notes.filter(n => n.pinned).length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="fixed bottom-24 right-6 z-40 space-y-2 max-w-[280px]"
          >
            {notes.filter(n => n.pinned).slice(0, 3).map((note) => (
              <motion.div
                key={note.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-gradient-to-br from-amber-500/15 to-amber-500/5 backdrop-blur-lg border border-amber-500/30 rounded-xl p-3 shadow-lg"
              >
                <div className="flex items-start gap-2">
                  <Pin size={12} className="text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-zinc-300 line-clamp-2">{note.content}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingWorkspaceWidget;
